// COMBAT V3 — the numbers. Pure tables + pure functions, imported by the engine
// (src/engine.js, ENGINE_VERSION 11) and by the presentation to PRINT the same
// numbers the referee uses. Every value that changes a resolved number lives
// here, so a tuning pass is one file and one ENGINE_VERSION bump.
//
// THE RULE IN ONE SENTENCE: pick a sigil, draw it; harder sigils hit harder,
// strike later, and fail harder.

import { TIER, sigilForRound, sigilTiming } from './sigils.js';
import { mulberry32 } from './rng.js';

// Engine action type -> sigil tier. The action TYPES are the v9 names on
// purpose: the presentation (arsenal set pieces, camera, SFX) already keys off
// them, and two other agents are rebuilding the action bar and camera on top of
// those names right now.
export const ACTION_TIER = Object.freeze({ basic: TIER.SLASH, heavy: TIER.RUNE, special: TIER.GLYPH, super: TIER.ULT, gastro: TIER.ULT });
export function tierOf(type) { return ACTION_TIER[type] || 0; }

// Damage multiplier, in PERCENT, from (tier, quality 0..100). v11: a STEEP
// piecewise-linear curve per tier (integer knots [q, pct], linear between) —
// precision pays, and the harder the tier the steeper the drop. Below the
// tier's line the sigil collapses to a flat percentage — that cliff IS the risk.
// The two rules it is built to (docs/COMBAT_V3.md §13, scripts/sigil-ev-sim.mjs):
//   a PERFECT Slash out-hits a moderate (q 70) Rune,
//   a PERFECT Rune out-hits a moderate (q 70) Glyph — even on a card whose
//   glyph ceiling is 1.33x its rune (the p90 of the card pool).
export const MULT = Object.freeze({
  1: Object.freeze({ below: 30, fizzle: 55, knots: [[30, 60], [50, 70], [70, 86], [85, 100], [100, 112]] }), // SLASH — a scribble still cuts
  2: Object.freeze({ below: 30, fizzle: 20, knots: [[30, 28], [50, 38], [70, 54], [85, 82], [100, 110]] }), // RUNE
  3: Object.freeze({ below: 50, fizzle: 5, knots: [[50, 22], [70, 44], [85, 76], [100, 112]] }),            // GLYPH — botch it and it fizzles
  4: Object.freeze({ below: 30, fizzle: 30, knots: [[30, 32], [50, 42], [70, 62], [85, 86], [100, 112]] }), // ULT — never nothing, never free
});
export const PERFECT_AT = 90;                       // the PERFECT word + SURGE_PERFECT
export const BLIND_BONUS = 15, BLIND_MIN_Q = 55;    // MEMORY mode: +15% if the blind trace is GOOD+
export const DEFAULT_Q = 70;                        // a strike with no trace data (search sims)

export function clampQ(q) {
  return Number.isInteger(q) ? Math.max(0, Math.min(100, q)) : null;
}
/** Integer percent from quality alone (time is timePct). Same inputs, same output, every client. */
export function multPct(tier, q, blind = false) {
  const m = MULT[tier] || MULT[1];
  const v = clampQ(q) ?? 0;
  if (v < m.below) return m.fizzle;
  const K = m.knots;
  let p = K[K.length - 1][1];
  for (let i = 1; i < K.length; i++) {
    if (v <= K[i][0]) { p = K[i - 1][1] + Math.floor(((K[i][1] - K[i - 1][1]) * (v - K[i - 1][0])) / (K[i][0] - K[i - 1][0])); break; }
  }
  if (blind && v >= BLIND_MIN_Q) p += BLIND_BONUS;
  return p;
}

// v11 TIME. `ms` = time from the guide appearing to the finger lifting (the
// action field `ms`, an integer). Per-sigil par/limit: src/sigil/sigils.js
// sigilTiming. Bonus only for a GOOD+ trace (a fast scribble earns nothing).
//   ms <= fast (60% of par)      +15%
//   fast..par                    +15% -> 0, linear
//   par..limit                   0 -> -25%, linear (the later, the worse)
//   ms >= limit (timed out)      x50% — a big hit, never zero
//   no ms (sims, old solo path)  100 (neutral)
export const TIME_BONUS_PCT = 15, TIME_LATE_PCT = 25, TIMEOUT_PCT = 50, TIME_BONUS_MIN_Q = 55;
export function timePct(sigil, ms, q = 100) {
  if (!Number.isInteger(ms) || ms < 0 || !sigil) return 100;
  const { par, fast, limit } = sigilTiming(sigil);
  if (ms >= limit) return TIMEOUT_PCT;
  if (ms > par) return 100 - Math.floor((TIME_LATE_PCT * (ms - par)) / (limit - par));
  if ((clampQ(q) ?? 0) < TIME_BONUS_MIN_Q) return 100;
  if (ms <= fast) return 100 + TIME_BONUS_PCT;
  return 100 + Math.floor((TIME_BONUS_PCT * (par - ms)) / (par - fast));
}
/** Quality x time, integer percent — THE number the engine multiplies damage by. */
export function traceMultPct(tier, q, blind, tp) {
  return Math.floor((multPct(tier, q, blind) * (Number.isInteger(tp) ? tp : 100)) / 100);
}
/**
 * The time percent for one attack action, from the action alone (lockstep: both
 * clients see the same action). An AI side rolls its time statelessly.
 */
export function actionTimePct(seed, round, side, a, q) {
  const tier = tierOf(a.type);
  const sg = sigilForRound(seed, round, side, tier);
  let ms = null;
  if (a.aiGlyph === true) ms = aiTraceMs(seed, round, side, tier, a.glyphDifficulty || 'normal', sigilTiming(sg).par);
  else if (Number.isInteger(a.ms)) ms = a.ms;
  return timePct(sg, ms, q);
}
export function fizzled(tier, q) { return (clampQ(q) ?? 0) < (MULT[tier] || MULT[1]).below; }

// Damage CEILINGS per tier, from the card's printed moves. SLASH is the basic
// move; RUNE is the v9 derived heavy (basic x1.6, nearest 5); GLYPH is at least
// the printed special and at least basic x1.8; ULT is the special x1.6.
export const ULT_MULT_PCT = 160;
export function heavyOf(basic) { return Math.round((basic * 1.6) / 5) * 5; }
export function glyphOf(basic, special) { return Math.max(special, Math.round((basic * 1.8) / 5) * 5); }
export function ultOf(special) { return Math.round((special * ULT_MULT_PCT) / 100); }

// Strike ORDER inside a round: faster sigils land first. Guard and swaps still
// resolve before any attack; supports before attacks.
export const PRIO = Object.freeze({
  swap: 4, brace: 4, spell: 3, trap: 3,
  basic: 2.2, heavy: 2.1, special: 2, super: 2, gastro: 2, focus: 1, pass: 0,
});
// A GLYPH that is still being drawn when a SLASH lands is STAGGERED.
export const STAGGER_PCT = 70;

// GUARD (engine type 'brace'): halves SLASH / ULT / supports, PARRIES a GLYPH
// (zero damage + counter), and is SHATTERED by a RUNE (full damage).
export const PARRY_COUNTER_PCT = 50;   // counter = 50% of the guard's SLASH ceiling, raw

// SURGE — the one meter. 0..100 per SIDE (it survives swaps). Full = ULT ready.
export const SURGE_MAX = 100;
export const SURGE_STRIKE = Object.freeze({ 1: 6, 2: 8, 3: 11, 4: 0 });     // x q/100
export const SURGE_PERFECT = 6;
export const SURGE_HURT_PCT = 30;      // taking 50% of max HP in one hit = +15
export const SURGE_PARRY = 30;
export const SURGE_GUARD = 8;          // not on two guards in a row
export const SURGE_PER_ENERGY = 5;     // v9 energy-denominated passives/synergies convert at 5:1
export const SURGE_KO_REWARD = 30;     // MOMENTUM passive (was +9 charge / +3 energy)
export const SURGE_HEIR = 40;          // LAST ORDERS (was full charge + 3 energy)
export const SURGE_START_PER_CHARGE = 15;
export const SURGE_PER_CHARGE_BOOST = 15;

export function strikeSurge(tier, q) {
  const v = clampQ(q) ?? 0;
  return Math.floor((v * (SURGE_STRIKE[tier] || 0)) / 100) + (v >= PERFECT_AT ? SURGE_PERFECT : 0);
}
export function hurtSurge(dealt, maxHp) {
  return maxHp > 0 ? Math.floor((dealt * SURGE_HURT_PCT) / maxHp) : 0;
}

// AI TRACE QUALITY. A stateless rng of its own (never st.rng), so either client
// can resolve an AI side locally and nothing later in the battle shifts.
// Means by difficulty and tier: the bot is good at slashes, shaky on glyphs.
// `human` is the PLAYER model the balance sims use for both referees: a guided
// touch trace by a first-week player. v11: re-pointed between the TYPICAL and
// GOOD hands of scripts/sigil-ev-sim.mjs (real scorer, synthetic strokes,
// 35-shape ladder), then NORMAL tuned to hold the engine-sim envelope — still an
// ASSUMPTION until prototype telemetry replaces it. The spread is narrower than
// v10's +-30 to match that sim's per-tier spread.
export const AI_Q = Object.freeze({
  human: Object.freeze({ 1: 93, 2: 88, 3: 86, 4: 82 }),
  easy: Object.freeze({ 1: 80, 2: 70, 3: 64, 4: 62 }),
  normal: Object.freeze({ 1: 91, 2: 85, 3: 81, 4: 78 }),
  wrath: Object.freeze({ 1: 95, 2: 91, 3: 88, 4: 86 }),
});
export const AI_Q_SPREAD = 16;
// ...and a LAPSE: AI_BOTCH_PCT of strokes go wrong outright (q 0..AI_BOTCH_TOP-1,
// MISS/OKAY) — the sim's hands lose their place now and then too, and a table
// that can never botch a GLYPH would make its fizzle risk fake.
export const AI_BOTCH_PCT = 5, AI_BOTCH_TOP = 45;
export function aiQuality(seed, round, side, tier, difficulty = 'normal') {
  const tab = AI_Q[difficulty] || AI_Q.normal;
  const mean = tab[tier] ?? tab[1];
  const sideBit = side === 'B' ? 1 : 0;
  const r = mulberry32((seed ^ Math.imul(round, 7919) ^ Math.imul(tier, 104729) ^ sideBit) >>> 0)();
  const b = AI_BOTCH_PCT / 100;
  if (r < b) return Math.floor((r / b) * AI_BOTCH_TOP);
  const q = mean + Math.floor((((r - b) / (1 - b)) * 2 - 1) * AI_Q_SPREAD);
  return Math.max(0, Math.min(100, q));
}

// AI TRACE TIME: a fraction of par per difficulty (percent), +-AI_T_SPREAD,
// from its own stateless roll (a different mix than aiQuality's).
export const AI_T = Object.freeze({ human: 92, easy: 112, normal: 100, wrath: 80 });
export const AI_T_SPREAD = 22;
export function aiTraceMs(seed, round, side, tier, difficulty, par) {
  const mean = AI_T[difficulty] ?? AI_T.normal;
  const sideBit = side === 'B' ? 1 : 0;
  const r = mulberry32((seed ^ Math.imul(round, 15485863) ^ Math.imul(tier, 32452843) ^ (sideBit << 7) ^ 0x7a3d) >>> 0)();
  const pct = mean + Math.floor((r * 2 - 1) * AI_T_SPREAD);
  return Math.max(0, Math.floor((par * pct) / 100));
}

// Expected multiplier (percent) for a difficulty's quality distribution —
// what the search AI and the lethal counter use instead of a single guess.
// Computed by exhaustive integer enumeration, so it is the same everywhere.
const EXP = {};
export function expectedMultPct(tier, difficulty = 'normal') {
  const k = `${difficulty}:${tier}`;
  if (EXP[k] != null) return EXP[k];
  const mean = (AI_Q[difficulty] || AI_Q.normal)[tier] ?? 70;
  let sum = 0, n = 0, bot = 0;
  for (let d = -AI_Q_SPREAD; d < AI_Q_SPREAD; d++) {
    sum += multPct(tier, Math.max(0, Math.min(100, mean + d)));
    n++;
  }
  for (let q = 0; q < AI_BOTCH_TOP; q++) bot += multPct(tier, q);
  EXP[k] = Math.floor(((100 - AI_BOTCH_PCT) * sum * AI_BOTCH_TOP + AI_BOTCH_PCT * bot * n) / (100 * n * AI_BOTCH_TOP));
  return EXP[k];
}
